<template>
  <section
    :id="`${cms.scroll_id}`"
    v-editable="cms"
    :class="[name, getClassName]"
    :style="mainCssVars"
  >
    <grid
      :class="[`${name}__grid`]"
      :layout="[
        {
          size: 1,
          columns: 'repeat(12, 1fr)',
          gap: '6px',
        },
        {
          size: 2,
          columns: 'repeat(24, 1fr)',
          gap: '10px',
        },
      ]"
    >
      <grid-item
        :class="[`${name}__grid-item`, `${name}__main`]"
        :layout="[
          { size: 1, x: 1, width: 12 },
          { size: 3, x: 1, width: 24 },
        ]"
      >
        <component
          :is="item.component"
          v-for="item in cms.main"
          :key="item._uid"
          v-editable="item"
          :cms="item"
          :class="`${name}__main-content`"
        />
      </grid-item>
      <grid-item
        :class="[`${name}__grid-item`, `${name}__overlay`]"
        :layout="[
          { size: 1, x: 1, width: 12 },
          { size: 3, x: 1, width: 24 },
        ]"
        :v-if="cms.overlay.length > 0"
      >
        <component
          :is="item.component"
          v-for="item in cms.overlay"
          :key="item._uid"
          v-editable="item"
          :cms="item"
        />
      </grid-item>
    </grid>
  </section>
</template>

<script>
import Colors from '~/mixins/colors'
import data from '~/components/organisms/o-article-v2/o-article-v2.data'
import MSlideV2 from '~/components/molecules/m-slide/m-slide-v2'
import MPractices from '~/components/molecules/m-practices/m-practices'

export default {
  components: { MSlideV2, MPractices },
  mixins: [Colors],
  props: {
    cms: {
      type: Object,
      default() {
        const config = {}
        return process.env.NODE_ENV === 'development'
          ? { ...data, ...config }
          : config
      },
    },
  },

  data() {
    return {
      name: 'o-wrapper-overlap',
    }
  },

  computed: {
    getClassName() {
      if (this.cms.overlay.length > 0) {
        return this.cms.overlap_position ? this.cms.overlap_position : ''
      }
      return ''
    },
    spacing() {
      return this.cms.spacing
    },
    fullwidth() {
      return this.cms.fullwidth || false
    },
    mainCssVars() {
      return {
        '--bg-color': this.setColor(
          (this.cms.color_background || {}).color || '#FFFFFF',
          this.cms.color_gradient
        ),
        '--bg-color-overlap': this.setColor(
          (this.cms.color_background_overlap || {}).color || 'transparent',
          this.cms.color_gradient
        ),
        '--text-color': this.setColor(
          (this.cms.color_text || {}).color || '#000000'
        ),
        '--hover-color': this.setColor(
          (this.cms.color_hover || {}).color || '#C33CA8'
        ),
        '--disabled-color': this.setColor(
          (this.cms.color_disabled || {}).color || '#888b8d'
        ),
        '--spacing-top': this.cms.spacing_top || '100px',
        '--spacing-bottom': this.cms.spacing_bottom || '100px',
        '--spacing-top-overlap': this.cms.spacing_top_overlap || 0,
        '--spacing-bottom-overlap': this.cms.spacing_bottom_overlap || 0,
        '--spacing-left-overlap': this.cms.spacing_left_overlap || 0,
        '--spacing-right-overlap': this.cms.spacing_right_overlap || 0,
        '--spacing-top-main': this.cms.spacing_top_main || 0,
        '--spacing-bottom-main': this.cms.spacing_bottom_main || 0,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.o-wrapper-overlap {
  padding: var(--spacing-top) 0 var(--spacing-bottom);
  &__overlay {
    z-index: 10;
    background-color: var(--bg-color-overlap);
    padding: var(--spacing-top-overlap) var(--spacing-right-overlap)
      var(--spacing-bottom-overlap) var(--spacing-left-overlap);
  }
  &.bottom-left {
    .o-wrapper-overlap {
      &__grid {
        grid-template-rows: auto 160px auto;
      }
      &__main {
        grid-row: 1/ 3;
      }
      &__overlay {
        grid-row: 2/ 4;
        grid-column: 1 / span 10;
      }
    }
  }
  &.bottom-right {
    .o-wrapper-overlap {
      &__grid {
        grid-template-rows: auto 160px auto;
      }
      &__main {
        grid-row: 1/ 3;
      }
      &__overlay {
        grid-row: 2/ 4;
        grid-column: 3 / span 10;
      }
    }
  }
  &.top-right {
    .o-wrapper-overlap {
      &__grid {
        grid-template-rows: auto 160px auto;
      }
      &__main {
        grid-row: 2/ 4;
      }
      &__overlay {
        grid-row: 1/ 3;
        grid-column: 3 / span 10;
      }
    }
  }
  &.top-left {
    .o-wrapper-overlap {
      &__grid {
        grid-template-rows: auto 160px auto;
      }
      &__main {
        grid-row: 2/ 4;
      }
      &__overlay {
        grid-row: 1/ 3;
        grid-column: 1 / span 10;
      }
    }
  }
  &.bottom-center {
    .o-wrapper-overlap {
      &__grid {
        grid-template-rows: auto 160px auto;
      }
      &__main {
        grid-row: 1/ 3;
      }
      &__overlay {
        grid-row: 2/ 4;
        grid-column: 2 / span 10;
      }
    }
  }
  ::v-deep &__main-content {
    padding-top: var(--spacing-top-main);
    padding-bottom: var(--spacing-bottom-main);
  }
}
@include media-breakpoint-up(md) {
  .o-wrapper-overlap {
    &.bottom-left {
      .o-wrapper-overlap {
        &__overlay {
          grid-column: 1 / span 15;
        }
      }
    }
    &.bottom-right {
      .o-wrapper-overlap__overlay {
        grid-column: 10 / span 15;
      }
    }
    &.top-right {
      .o-wrapper-overlap__overlay {
        grid-column: 10 / span 15;
      }
    }
    &.top-left {
      .o-wrapper-overlap {
        &__overlay {
          grid-column: 1 / span 15;
        }
      }
    }
    &.bottom-center {
      .o-wrapper-overlap {
        &__overlay {
          grid-column: 4 / span 17;
        }
      }
    }
  }
}
</style>
